import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, FileCode, FileSpreadsheet, CheckCircle, Truck, FileText, FileDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';

export default function DispatchPage() {  // eslint-disable-line
  const { t } = useTranslation();
  const [dispatches, setDispatches] = useState([]);
  const [certificates, setCerts] = useState([]);
  const [selectedCerts, setSelectedCerts] = useState([]);
  // Příjemce se neodvozuje ručně — bere se zákazník z vybraných KL (viz recipientInfo níže).
  // Pre-dispatch checklist + tracking
  const [trackingNumber, setTrackingNumber] = useState('');
  const [checklist, setChecklist] = useState({
    cleaned: false,
    allPartsIncluded: false,
    sealsIntact: false,
  });

  const removeDispatch = async (d) => {
    if (!window.confirm(`Smazat výdejní protokol ${d.protocolNumber || ''}? Tuto akci nelze vrátit.`)) return;
    try {
      await api.delete(`/dispatches/${d.id}`);
      load();
      toast.success('Výdejní protokol smazán');
    } catch (err) {
      toastApiError(err);
    }
  };

  const load = () => {
    api.get('/dispatches').then(r => setDispatches(r.data)).catch(() => {});
    // Schválené master kalibrační listy = přístroje připravené k výdeji.
    api.get('/master-certificates?status=approved').then(r => setCerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleCert = (certId) => {
    setSelectedCerts(prev =>
      prev.includes(certId) ? prev.filter(c => c !== certId) : [...prev, certId]
    );
  };

  // KL už použité v nějakém výdeji — nesmí se znovu nabízet.
  const dispatchedCertIds = new Set(
    dispatches.flatMap(d => d.calibrations || [])
  );
  const availableCerts = certificates.filter(c => !dispatchedCertIds.has(c.id));

  // Příjemce = zákazník(ci) z vybraných KL. Read-only, needituje se ručně.
  const recipientNames = [...new Set(
    availableCerts
      .filter(c => selectedCerts.includes(c.id))
      .map(c => c.customerName)
      .filter(Boolean)
  )];
  const recipientInfo = recipientNames.join(', ');

  // Pre-dispatch kontrola jako viditelná brána: progres + blokace tlačítka.
  const checklistItems = [
    { key: 'cleaned', label: t('dispatches.checklist-cleaned') },
    { key: 'allPartsIncluded', label: t('dispatches.checklist-all-parts') },
    { key: 'sealsIntact', label: t('dispatches.checklist-seals') },
  ];
  const checklistDoneCount = checklistItems.filter(i => checklist[i.key]).length;
  const checklistComplete = checklistDoneCount === checklistItems.length;
  const canCreateDispatch = selectedCerts.length > 0 && checklistComplete;

  const downloadDccXml = (certId, certNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/master-certificates/${certId}/dcc-xml`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${certNumber || 'DCC'}.xml`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error(t('dispatches.error-dcc-unavailable')));
  };

  const downloadDispatchPdf = (dispatchId, protocolNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/dispatches/${dispatchId}/protocol.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.detail || t('dispatches.error-pdf-cannot-download')); });
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${protocolNumber || t('dispatches.protocol-filename-prefix')}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => toastApiError(err, t('dispatches.error-pdf-unavailable')));
  };

  const createDispatch = async () => {
    if (selectedCerts.length === 0) {
      toast.error(t('dispatches.error-no-instruments'));
      return;
    }
    if (!checklist.cleaned || !checklist.allPartsIncluded || !checklist.sealsIntact) {
      const missing = [];
      if (!checklist.cleaned) missing.push(t('dispatches.checklist-missing-cleaned'));
      if (!checklist.allPartsIncluded) missing.push(t('dispatches.checklist-missing-parts'));
      if (!checklist.sealsIntact) missing.push(t('dispatches.checklist-missing-seals'));
      toast.error(t('dispatches.error-checklist-missing', { missing: missing.join(', ') }));
      return;
    }
    try {
      await api.post('/dispatches', {
        calibrations: selectedCerts,
        recipientInfo,
        notes: '',
        preDispatchChecklist: checklist,
        trackingNumber: trackingNumber.trim(),
      });
      setSelectedCerts([]);
      setTrackingNumber('');
      setChecklist({ cleaned: false, allPartsIncluded: false, sealsIntact: false });
      load();
      toast.success(t('dispatches.toast-created'));
    } catch (err) { toastApiError(err, t('dispatches.error-default')); }
  };

  // XLS export pro fakturaci v ERP
  const exportXlsx = () => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${baseUrl}/api/dispatches/export.xlsx`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => {
        if (!blob) throw new Error(t('dispatches.error-export'));
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${t('dispatches.xls-filename-prefix')}-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => toastApiError(err, t('dispatches.error-export')));
  };

  // Confirm delivery (POD od přepravce)
  const confirmDelivery = async (dispatchId) => {
    try {
      await api.put(`/dispatches/${dispatchId}/confirm-delivery`, {});
      toast.success(t('dispatches.toast-delivered'));
      load();
    } catch (err) { toastApiError(err); }
  };

  return (
    <div data-testid="dispatch-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Left: Available instruments */}
      <div className="flex-1 flex flex-col gap-4">
        <div className="ct-panel flex-1 flex flex-col">
          <div className="ct-panel-header">
            {t('dispatches.available-instruments-title')}
          </div>
          <ScrollArea className="flex-1">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                  <th className="w-8 py-2 px-2"></th>
                  <th className="text-left py-2 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('dispatches.col-instrument')}</th>
                  <th className="text-left py-2 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('dispatches.col-instrument-type')}</th>
                  <th className="text-left py-2 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('dispatches.col-cert-number')}</th>
                  <th className="text-left py-2 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('dispatches.col-date')}</th>
                  <th className="text-center py-2 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('dispatches.col-dcc')}</th>
                </tr>
              </thead>
              <tbody>
                {availableCerts.map(cert => (
                  <tr key={cert.id} className="ct-table-row cursor-pointer" onClick={() => toggleCert(cert.id)}>
                    <td className="py-1.5 px-2">
                      <input
                        type="checkbox"
                        checked={selectedCerts.includes(cert.id)}
                        onChange={() => toggleCert(cert.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="py-1.5 px-2">{cert.instrumentName}</td>
                    <td className="py-1.5 px-2 font-mono text-xs">{cert.instrumentType}</td>
                    <td className="py-1.5 px-2 font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{cert.certificateNumber}</td>
                    <td className="py-1.5 px-2 font-mono text-xs">{cert.approvedAt ? new Date(cert.approvedAt).toLocaleDateString(t('common.date-locale')) : ''}</td>
                    <td className="py-1.5 px-2 text-center">
                      {cert.hasDccXml && (
                        <button
                          data-testid={`dispatch-dcc-${cert.id}`}
                          onClick={(e) => { e.stopPropagation(); downloadDccXml(cert.id, cert.certificateNumber); }}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors"
                          style={{ color: 'var(--ct-accent)', border: '1px solid var(--ct-border)' }}
                          title={t('dispatches.btn-download-dcc-title')}
                        >
                          <FileCode className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {availableCerts.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8" style={{ color: 'var(--ct-text-disabled)' }}>{t('dispatches.empty-instruments')}</td></tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
        </div>

        {/* New dispatch form */}
        <div className="ct-panel">
          <div className="ct-panel-header flex items-center justify-between">
            <span>{t('dispatches.new-dispatch-title')}</span>
            <button
              onClick={exportXlsx}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors"
              style={{ color: 'var(--ct-accent)', border: '1px solid var(--ct-border)' }}
              title={t('dispatches.btn-export-xls-title')}
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> {t('dispatches.btn-export-xls')}
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="ct-form-field">
                <label className="ct-form-label">{t('dispatches.label-recipient')}</label>
                <div
                  className="h-9 flex items-center px-3 rounded text-sm"
                  style={{
                    background: 'var(--ct-surface)',
                    border: '1px solid var(--ct-border)',
                    color: recipientInfo ? 'var(--ct-text)' : 'var(--ct-text-disabled)',
                  }}
                  title={t('dispatches.recipient-from-cert-title')}
                >
                  {recipientInfo || t('dispatches.recipient-empty')}
                </div>
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">{t('dispatches.label-tracking')}</label>
                <Input
                  value={trackingNumber}
                  onChange={e => setTrackingNumber(e.target.value)}
                  placeholder={t('dispatches.label-tracking-placeholder')}
                  className="h-9 text-sm"
                />
              </div>
            </div>
            {/* Pre-dispatch kontrola — viditelná brána před výdejem */}
            <div
              className="rounded-md p-3"
              style={{
                border: `1px solid ${checklistComplete ? 'var(--ct-success)' : 'var(--ct-border)'}`,
                background: checklistComplete ? 'var(--ct-success-bg, rgba(34,197,94,0.06))' : 'var(--ct-surface)',
                transition: 'border-color 150ms ease, background 150ms ease',
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="ct-form-label font-medium" style={{ color: 'var(--ct-text)' }}>
                  {t('dispatches.checklist-title')}
                </span>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded-full"
                  style={{
                    color: checklistComplete ? 'var(--ct-success)' : 'var(--ct-text-secondary)',
                    background: 'var(--ct-bg)',
                    border: '1px solid var(--ct-border)',
                  }}
                >
                  {t('dispatches.checklist-progress', { done: checklistDoneCount, total: checklistItems.length })}
                </span>
              </div>
              <p className="text-xs mb-2" style={{ color: 'var(--ct-text-secondary)' }}>
                {t('dispatches.checklist-subtitle')}
              </p>
              <div className="flex flex-col gap-1 text-sm">
                {checklistItems.map(item => {
                  const checked = !!checklist[item.key];
                  return (
                    <label
                      key={item.key}
                      className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5"
                      style={{
                        background: checked ? 'var(--ct-success-bg, rgba(34,197,94,0.08))' : 'transparent',
                        color: checked ? 'var(--ct-success)' : 'var(--ct-text)',
                        transition: 'background 120ms ease, color 120ms ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => setChecklist({ ...checklist, [item.key]: e.target.checked })}
                      />
                      {checked
                        ? <CheckCircle className="w-4 h-4" style={{ color: 'var(--ct-success)' }} />
                        : <span className="inline-block w-4 h-4 rounded-full" style={{ border: '2px solid var(--ct-border)' }} />}
                      <span style={{ fontWeight: checked ? 500 : 400 }}>{item.label}</span>
                    </label>
                  );
                })}
              </div>
              {checklistComplete && (
                <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'var(--ct-success)' }}>
                  <CheckCircle className="w-3.5 h-3.5" /> {t('dispatches.checklist-done')}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="create-dispatch-btn"
                onClick={createDispatch}
                disabled={!canCreateDispatch}
                title={!canCreateDispatch ? t('dispatches.btn-create-dispatch-blocked') : undefined}
                className="ct-btn-primary gap-1"
              >
                <Send className="w-4 h-4" /> {t('dispatches.btn-create-dispatch')}
              </Button>
              <span className="text-xs self-center" style={{ color: 'var(--ct-text-secondary)' }}>
                {t('dispatches.selected-count', { count: selectedCerts.length })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Dispatch protocols */}
      <div className="ct-panel w-80 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center gap-2">
          <FileText className="w-4 h-4" />
          {t('dispatches.protocols-title')}
        </div>
        <ScrollArea className="flex-1">
          {dispatches.map(d => (
            <div
              key={d.id}
              className="ct-table-row px-3 py-3 text-sm"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono" style={{ color: 'var(--ct-accent)' }}>{d.protocolNumber}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono" style={{ color: 'var(--ct-text-secondary)' }}>
                    {d.dispatchedAt ? new Date(d.dispatchedAt).toLocaleDateString(t('common.date-locale')) : ''}
                  </span>
                  <button
                    onClick={() => downloadDispatchPdf(d.id, d.protocolNumber)}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{ color: 'var(--ct-accent)', border: '1px solid var(--ct-accent)' }}
                    title={t('dispatches.btn-download-protocol-title')}
                  >
                    <FileDown className="w-3 h-3" /> PDF
                  </button>
                  <button
                    onClick={() => removeDispatch(d)}
                    className="p-1 rounded"
                    style={{ color: 'var(--ct-error, #dc2626)' }}
                    title="Smazat výdejní protokol"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="text-xs mt-1" style={{ color: 'var(--ct-text-secondary)' }}>
                {t('dispatches.dispatched-by-prefix')} {d.dispatchedByName || '—'} | {t('dispatches.recipient-prefix')} {d.recipientInfo || '—'}
              </div>
              {/* Tracking + delivery confirmation — potvrdit doručení jde vždy (i bez trackingu, např. osobní odběr). */}
              {(
                <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                  {d.trackingNumber && (
                    <span className="font-mono inline-flex items-center gap-1"
                      style={{ color: 'var(--ct-info)' }}>
                      <Truck className="w-3 h-3" /> {d.trackingNumber}
                    </span>
                  )}
                  {d.deliveryConfirmedAt ? (
                    <span className="inline-flex items-center gap-1"
                      style={{ color: 'var(--ct-success)' }}>
                      <CheckCircle className="w-3 h-3" />
                      {t('dispatches.delivered-prefix')} {new Date(d.deliveryConfirmedAt).toLocaleDateString(t('common.date-locale'))}
                    </span>
                  ) : (
                    <button
                      onClick={() => confirmDelivery(d.id)}
                      className="text-xs underline"
                      style={{ color: 'var(--ct-accent)' }}
                    >
                      {t('dispatches.btn-confirm-delivery')}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {dispatches.length === 0 && (
            <div className="p-4 text-center text-sm" style={{ color: 'var(--ct-text-disabled)' }}>{t('dispatches.empty-protocols')}</div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
